/* eslint-disable no-nested-ternary */
import groupBy from 'lodash/groupBy';
import gql from 'graphql-tag';
import SubmissionDetailsFragment from '../apollo/fragments/submissionDetailsFragment.graphql';
import { studentListFilters } from '../components/AssignmentClassDashboard/studentsListFilter';
import { buildSubmissionListFromGroupedSubmissions } from './buildSubmissionListFromGroupedSubmissions';

const calculateAverage = (
  previousAverage,
  submissions,
  returnedCount,
  maxScore,
) => {
  if (maxScore) {
    const sumOfReturnedSubmissionGrades = submissions
      .filter(s => s.submission && s.submission.status === 'RETURNED')
      .reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.submission.response.score,
        0,
      );

    return (
      (sumOfReturnedSubmissionGrades / (returnedCount || 1) / maxScore) * 100
    );
  }

  return previousAverage;
};

const calculateTheCounts = (submissions, assignmentStats, maxScore) => {
  const { average } = assignmentStats;
  const updatedCounts = submissions.reduce(
    (acc, s) => ({
      submissionsCount:
        s.submission &&
        (s.submission.status === 'SUBMITTED' ||
          s.submission.status === 'EVALUATED' ||
          s.submission.status === 'RETURNED')
          ? acc.submissionsCount + 1
          : acc.submissionsCount,
      evaluatedCount:
        s.submission &&
        (s.submission.status === 'EVALUATED' ||
          s.submission.status === 'RETURNED')
          ? acc.evaluatedCount + 1
          : acc.evaluatedCount,
      returnedCount:
        s.submission && s.submission.status === 'RETURNED'
          ? acc.returnedCount + 1
          : acc.returnedCount,
    }),
    {
      submissionsCount: 0,
      evaluatedCount: 0,
      returnedCount: 0,
    },
  );

  return {
    ...assignmentStats,
    ...updatedCounts,
    average: calculateAverage(
      average,
      submissions,
      updatedCounts.returnedCount,
      maxScore,
    ),
  };
};

const groupSubmissionsByState = (
  cachedGroupedSubmissions,
  studentSubmissions,
  requireSubmission,
) => {
  const groupedSubmissions = groupBy(
    studentSubmissions,
    studentsSubmission =>
      studentsSubmission.submission === null ||
      (studentsSubmission.submission.status === 'SUBMITTED' &&
        !requireSubmission)
        ? 'PENDING'
        : studentsSubmission.submission.status,
  );
  return {
    ...cachedGroupedSubmissions,
    pendingSubmission: requireSubmission
      ? groupedSubmissions.PENDING
        ? groupedSubmissions.PENDING
        : []
      : null,
    pendingEvaluation: requireSubmission
      ? groupedSubmissions.SUBMITTED || []
      : groupedSubmissions.PENDING || [],
    pendingReturn: groupedSubmissions.EVALUATED
      ? groupedSubmissions.EVALUATED
      : [],
    returned: groupedSubmissions.RETURNED ? groupedSubmissions.RETURNED : [],
  };
};

export const afterSubmissionResponseReturn = ({
  isReturning = false,
  attachmentRequired,
  commentRequired,
}) => (cache, { data }) => {
  const response = isReturning
    ? data.submissionsReturn
    : [data.submissionResponse];

  response.forEach(r => {
    const requireSubmission = attachmentRequired || commentRequired;

    const getGroupedSubmissionsFragment = key => ({
      id: `GroupedSubmissions:${key}`,
      fragmentName: 'GroupedSubmissionsFragment',
      fragment: gql`
        ${SubmissionDetailsFragment}

        fragment GroupedSubmissionsFragment on GroupedSubmissions {
          pendingSubmission {
            ...SubmissionDetailsFragment
          }
          pendingEvaluation {
            ...SubmissionDetailsFragment
          }
          pendingReturn {
            ...SubmissionDetailsFragment
          }
          returned {
            ...SubmissionDetailsFragment
          }
        }
      `,
    });

    studentListFilters.forEach(({ key }) => {
      const groupedSubmissionFromCache = cache.readFragment(
        getGroupedSubmissionsFragment(key),
      );

      if (!groupedSubmissionFromCache) {
        console.warn(
          'Did not find grouped submissions under the key: ',
          `GroupedSubmissions:${key}`,
        );
        return;
      }

      const updatedSubmissionsList = buildSubmissionListFromGroupedSubmissions(
        groupedSubmissionFromCache,
      ).map(s => {
        if (
          (s.submission && s.submission.id === r.id) ||
          (s.student && s.student.id === r.studentId)
        ) {
          return {
            ...s,
            student: s.student,
            submission: r,
          };
        }
        return { ...s, submission: s.submission, student: s.student };
      });

      const updatedGroupedSubmissions2 = groupSubmissionsByState(
        groupedSubmissionFromCache,
        updatedSubmissionsList,
        requireSubmission,
      );

      cache.writeFragment({
        ...getGroupedSubmissionsFragment(key),
        data: updatedGroupedSubmissions2,
      });
    });

    const updatedGroupedSubmissions = cache.readFragment(
      getGroupedSubmissionsFragment(studentListFilters[0].key),
    );

    const updatedSubmissions = buildSubmissionListFromGroupedSubmissions(
      updatedGroupedSubmissions,
    );

    const assignmentStatsFragment = {
      id: 'AssignmentStats',
      fragment: gql`
        fragment AssignmentStatsFragment on AssignmentStats {
          assignedStudentsCount
          submissionsCount
          evaluatedCount
          returnedCount
          average
        }
      `,
    };

    const cachedAssignmentStats = cache.readFragment(assignmentStatsFragment);

    const cachedAssignmentInstanceResponse = cache.readFragment({
      id: 'AssignmentInstanceResponse',
      fragment: gql`
        fragment AssignmentInstanceResponseFragment on AssignmentInstanceResponse {
          maxScore
        }
      `,
    });

    if (!cachedAssignmentInstanceResponse || !cachedAssignmentStats) {
      console.warn(
        'Could not update local cache as either AssignmentInstanceResponse or AssignmentStats is not cached',
      );
      return;
    }

    cache.writeFragment({
      ...assignmentStatsFragment,
      data: calculateTheCounts(
        updatedSubmissions,
        cachedAssignmentStats,
        cachedAssignmentInstanceResponse.maxScore || null,
      ),
    });
  });
};
